// Хичээл үзэх — play дуудаад mode-оор нь: stream (<video>), embed (Drive iframe), doc (HTML/PDF гарын авлага), link (гадаад холбоос)
// stream үед үзэлтийг 15 секунд тутам, зогсоох/дуусах/хуудас нуугдах үед бүртгэнэ.
// doc үед хуудас харагдаж байх хооронд 15 секунд тутам дохио → уншсан хугацаа; доош гүйлгэж дуусахад "Уншиж танилцлаа".
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Spin } from "antd";
import { FiChevronLeft, FiMaximize, FiPlay, FiShield, FiFileText, FiExternalLink, FiCheck, FiClock } from "react-icons/fi";
import { instance } from "utils/axios";
import ls from "utils/ls";
import { AuthContext } from "contexts";
import { API_ORIGIN, fmtDur, itemPath } from "./common";

const RATES = [1, 1.25, 1.5];
const REPORT_MS = 15000;
// Усан тэмдэг байрлалаа үе үе солино
const WM_POS = ["left-3 top-2.5", "right-3 top-2.5", "right-3 bottom-14", "left-3 bottom-14"];

// Үзэлт бүртгэх — keepalive тул хуудас хаагдах үед ч явна
const sendProgress = (itemId, body) => {
  const token = ls.get("studentInfo")?.token;
  return fetch(`${process.env.REACT_APP_MAIN_API_URL}/learning/items/${itemId}/progress`, {
    method: "POST",
    keepalive: true,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  }).then((r) => (r.ok || r.status === 400 ? r.json() : null)).catch(() => null); // 400: { error }
};

// Гарын авлага унших: апп-ын хуудасны нэг хэсэг шиг — HTML нь sandbox-той iframe, өндрийг баримт өөрөө мэдээлнэ
// (API-ийн DOC_BRIDGE), тиймээс хуудас нэг л гүйлгэнэ. Доод "Уншиж танилцлаа" хүртэл гүйлгэж очмогц баталгаажуулна.
function DocReader({ itemId, play, title, seriesTitle, backTo }) {
  const [readSec, setReadSec] = useState(play.readSec || 0);
  const [done, setDone] = useState(!!play.completed);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [height, setHeight] = useState(null);
  const [reached, setReached] = useState(false);
  const frameRef = useRef(null);
  const endRef = useRef(null);
  const isHtml = play.fileType === "html";
  const need = play.minReadSec || 0;

  useEffect(() => {
    const beat = () => {
      if (document.visibilityState !== "visible") return;
      setReadSec((x) => x + REPORT_MS / 1000);
      sendProgress(itemId, { viewId: play.viewId, beat: REPORT_MS / 1000 });
    };
    const t = setInterval(beat, REPORT_MS);
    return () => clearInterval(t);
  }, [itemId, play.viewId]);

  // Баримтаас: {tasDoc:"size",h} — өндөр, {tasDoc:"scroll",y} — агуулгын холбоос
  useEffect(() => {
    if (!isHtml) return undefined;
    const onMessage = (e) => {
      const f = frameRef.current;
      const m = e.data;
      if (!f || e.source !== f.contentWindow || !m || typeof m !== "object") return;
      if (m.tasDoc === "size" && m.h > 0) setHeight(Math.min(Number(m.h), 200000));
      if (m.tasDoc === "scroll") window.scrollTo({ top: f.getBoundingClientRect().top + window.scrollY + Number(m.y) - 72, behavior: "smooth" });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [isHtml]);

  // Доод хэсэг харагдмагц уншиж дууссанд тооцно (HTML бол бодит өндөр ирсний дараа)
  useEffect(() => {
    const el = endRef.current;
    if (!el || reached || (isHtml && !height)) return undefined;
    const io = new IntersectionObserver(([x]) => { if (x.isIntersecting) setReached(true); });
    io.observe(el);
    return () => io.disconnect();
  }, [reached, isHtml, height]);

  const confirm = () => {
    setSaving(true);
    setErr("");
    sendProgress(itemId, { viewId: play.viewId, done: true }).then((r) => {
      setSaving(false);
      if (r?.completed) setDone(true);
      else setErr(r?.error || r?.value || "Хадгалж чадсангүй, дахин оролдоно уу");
    });
  };
  const left = Math.max(0, need - readSec);
  const min = (sec) => Math.max(1, Math.ceil(sec / 60));

  return (
    <div className="pb-6 max-w-3xl mx-auto md:pt-6">
      <Link to={backTo} className="inline-flex items-center gap-0.5 h-10 mt-2 md:mt-0 pr-2 text-[15px] font-semibold text-primary">
        <FiChevronLeft size={20} /> Буцах
      </Link>
      <div className="pt-2">
        {seriesTitle && <div className="text-[13px] font-semibold text-primary">{seriesTitle}</div>}
        <h1 className="text-xl font-bold mt-1">{title}</h1>
      </div>
      <div className="pcard overflow-hidden mt-4">
        <iframe
          ref={frameRef}
          key={play.src}
          src={API_ORIGIN + play.src}
          title={title || "Гарын авлага"}
          {...(isHtml ? { sandbox: "allow-scripts allow-popups allow-popups-to-escape-sandbox", scrolling: "no" } : {})}
          style={isHtml ? { height: height || 600 } : undefined}
          // iOS Safari iframe-ийг агуулгын өргөнөөр сунгадаг — w-px + min-w-full нь өргөнд барина
          className={`block w-px min-w-full border-0 bg-white ${isHtml ? "" : "h-[75dvh]"}`}
        />
      </div>
      <section ref={endRef} className="pcard pcard-pad mt-3.5 flex items-center gap-3">
        {done ? (
          <>
            <span className="h-10 w-10 rounded-full bg-[#22c55e] text-white flex items-center justify-center shrink-0"><FiCheck size={20} strokeWidth={3} /></span>
            <span className="text-[15px] font-bold text-[#15803d]">Танилцсан</span>
          </>
        ) : (
          <>
            <span className="h-10 w-10 rounded-xl bg-[#f3eeff] text-primary flex items-center justify-center shrink-0"><FiClock size={18} /></span>
            <span className="flex-1 min-w-0 text-[13px] leading-snug text-gray-600">
              {reached && left > 0 ? <><b>{min(left)} мин</b> үлдлээ</> : "Уншиж дууссан уу?"}
              {err && <span className="block text-red-600 mt-0.5">{err}</span>}
            </span>
            <button onClick={confirm} disabled={!reached || left > 0 || saving}
              className="h-11 px-4 rounded-full bg-primary text-white text-[14px] font-bold disabled:bg-[#d9cdea] shrink-0">
              {saving ? "…" : "Уншиж танилцлаа"}
            </button>
          </>
        )}
      </section>
    </div>
  );
}

function Player() {
  const { itemId } = useParams();
  const [sp] = useSearchParams();
  const seriesId = sp.get("series");
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useContext(AuthContext);
  const profile = state.studentInfo?.profile || {};

  const [series, setSeries] = useState(null);
  const [play, setPlay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rate, setRate] = useState(1);
  const [wm, setWm] = useState(0);
  const videoRef = useRef(null);
  const vidEl = useRef(null); // React ref-ийг unmount үед null болгодог тул элементийг тусад нь хадгална
  const boxRef = useRef(null);
  const timerRef = useRef(null);
  const lastSent = useRef(-1);
  const playingId = useRef(null); // одоо тоглож буй хичээл (itemId солигдох үед хуучныг нь бүртгэхэд)

  useEffect(() => {
    if (!seriesId) return;
    instance({ method: "get", url: `/learning/series/${seriesId}` })
      .then((res) => setSeries(res.data || null))
      .catch(() => {});
  }, [seriesId]);

  useEffect(() => {
    let alive = true;
    setPlay(null);
    setLoading(true);
    instance({ method: "post", url: `/learning/items/${itemId}/play`, data: { source: "portal" }, silent: true })
      .then((res) => {
        if (!alive) return;
        playingId.current = itemId;
        vidEl.current = null;
        lastSent.current = -1;
        setPlay(res.data || { mode: "none" }); // 204 — харагдахгүй
      })
      .catch(() => alive && setPlay({ mode: "none" }))
      .then(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [itemId]);

  // Одоогийн байрлалыг бүртгэх (давхар илгээхгүй)
  const report = () => {
    const v = vidEl.current;
    if (!v || !playingId.current || !v.duration || !isFinite(v.duration)) return;
    const pos = Math.floor(v.currentTime);
    if (pos === lastSent.current) return;
    lastSent.current = pos;
    sendProgress(playingId.current, { position: pos, duration: Math.round(v.duration), viewId: play?.viewId });
  };
  const reportRef = useRef(report);
  reportRef.current = report;
  const stopTimer = () => { clearInterval(timerRef.current); timerRef.current = null; };

  // Хуудас нуугдах/гарах үед бүртгэнэ
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "hidden") reportRef.current(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      stopTimer();
      reportRef.current();
    };
  }, [itemId]);

  useEffect(() => { if (videoRef.current) videoRef.current.playbackRate = rate; }, [rate]);
  useEffect(() => {
    if (play?.mode !== "stream") return undefined;
    const t = setInterval(() => setWm((x) => (x + 1) % WM_POS.length), 20000);
    return () => clearInterval(t);
  }, [play?.mode]);

  const items = series?.items || [];
  const idx = items.findIndex((i) => String(i.id) === String(itemId));
  const cur = idx >= 0 ? items[idx] : null;
  const next = idx >= 0 ? items[idx + 1] : null;
  const backTo = seriesId ? `/learning/${seriesId}` : "/learning";
  const mobile = String(profile.mobile || "").replace(/\D/g, "");
  const watermark = [profile.firstName, mobile.length >= 4 ? `****${mobile.slice(-4)}` : null].filter(Boolean).join(" · ");

  const onMeta = (e) => {
    const v = e.currentTarget;
    vidEl.current = v;
    v.playbackRate = rate;
    if (play?.startAt > 0 && play.startAt < v.duration - 3) v.currentTime = play.startAt;
  };
  const onPlay = () => {
    stopTimer();
    timerRef.current = setInterval(() => reportRef.current(), REPORT_MS);
  };
  const onPause = () => { stopTimer(); report(); };
  const onEnded = () => {
    stopTimer();
    report();
    if (next) navigate(itemPath(next.id, seriesId), { replace: true, state: { autoplay: true } });
  };
  const fullscreen = () => {
    const box = boxRef.current;
    const v = videoRef.current;
    if (document.fullscreenElement) document.exitFullscreen();
    else if (box?.requestFullscreen) box.requestFullscreen().catch(() => {});
    else if (v?.webkitEnterFullscreen) v.webkitEnterFullscreen(); // iPhone
  };
  const counter = items.length && idx >= 0 ? `${idx + 1} / ${items.length}` : "";
  const isVideoMode = play?.mode === "stream" || play?.mode === "embed";

  if (!loading && play?.mode === "doc") {
    return <DocReader itemId={itemId} play={play} title={cur?.title} seriesTitle={series?.title} backTo={backTo} />;
  }

  return (
    <div className="pb-6 max-w-3xl mx-auto md:pt-6">
      {/* Дээд хэсэг — видео (хар дэвсгэр) */}
      {isVideoMode ? (
        <div className="-mx-4 md:mx-0 md:rounded-2xl overflow-hidden bg-black">
          <div className="h-11 px-2 flex items-center justify-between">
            <Link to={backTo} aria-label="Буцах" className="h-11 w-11 flex items-center justify-center text-white hover:text-white"><FiChevronLeft size={22} /></Link>
            <span className="text-[13px] font-semibold text-white/80">{counter}</span>
            {play.mode === "stream" ? (
              <button onClick={fullscreen} aria-label="Бүтэн дэлгэц" className="h-11 w-11 flex items-center justify-center text-white"><FiMaximize size={18} /></button>
            ) : <span className="w-11" />}
          </div>
          <div ref={boxRef} className="relative bg-black">
            {play.mode === "stream" ? (
              <video
                key={play.src}
                ref={videoRef}
                src={API_ORIGIN + play.src}
                className="block w-full aspect-video bg-black [:fullscreen_&]:h-full"
                controls
                playsInline
                autoPlay={!!location.state?.autoplay}
                controlsList="nodownload noplaybackrate nofullscreen"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                onLoadedMetadata={onMeta}
                onPlay={onPlay}
                onPause={onPause}
                onEnded={onEnded}
              />
            ) : (
              <div className="relative w-full pt-[56.25%]">
                <iframe key={play.embedUrl} src={play.embedUrl} title={cur?.title || "Бичлэг"} allow="autoplay; fullscreen" allowFullScreen
                  className="absolute inset-0 h-full w-full border-0" />
              </div>
            )}
            {/* Усан тэмдэг — үзэж буй хүний нэр, дугаар */}
            {watermark && (
              <span className={`pointer-events-none select-none absolute ${WM_POS[wm]} text-[11px] font-semibold text-white/35 transition-all duration-700`}>
                {watermark}
              </span>
            )}
          </div>
        </div>
      ) : (
        <Link to={backTo} className="inline-flex items-center gap-0.5 h-10 mt-2 md:mt-0 pr-2 text-[15px] font-semibold text-primary">
          <FiChevronLeft size={20} /> Буцах
        </Link>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Spin /></div>
      ) : (
        <div className="flex flex-col gap-3.5 pt-4">
          <div>
            {series && <div className="text-[13px] font-semibold text-primary">{series.title}</div>}
            <h1 className="text-xl font-bold mt-1">{cur?.title || ""}</h1>
          </div>

          {play?.mode === "none" && (
            <section className="pcard pcard-pad text-center py-10 text-gray-400 text-sm">Энэ хичээл олдсонгүй</section>
          )}

          {play?.mode === "stream" && (
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-gray-700">Хурд</span>
              {RATES.map((r) => (
                <button key={r} onClick={() => setRate(r)} aria-pressed={rate === r} className={`pchip ${rate === r ? "pchip-on" : "pchip-off"}`}>{r}x</button>
              ))}
            </div>
          )}

          {/* embed (Drive цонх): зөвхөн нээсэн нь бүртгэгдэнэ — гараар "үзсэн" тэмдэглэхгүй */}

          {play?.mode === "link" && (
            play.url ? (
              <section className="pcard pcard-pad flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <span className="h-11 w-11 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center shrink-0"><FiFileText size={22} /></span>
                  <span className="text-sm text-gray-600">Гарын авлага шинэ цонхонд нээгдэнэ. Уншаад “Уншсан” гэж тэмдэглээрэй.</span>
                </div>
                <a href={play.url} target="_blank" rel="noopener noreferrer"
                  className="h-12 rounded-xl bg-primary text-white hover:text-white text-[15px] font-bold flex items-center justify-center gap-2">
                  <FiExternalLink /> Нээх
                </a>
              </section>
            ) : (
              <section className="pcard pcard-pad text-center py-10 text-gray-400 text-sm">Удахгүй нэмэгдэнэ</section>
            )
          )}

          {/* Дараагийн хэсэг */}
          {next && (
            <Link to={itemPath(next.id, seriesId)} className="pcard flex items-center gap-3 p-3 text-gray-900 hover:text-gray-900">
              <span className="h-[50px] w-[88px] rounded-[10px] bg-[#1f1630] flex items-center justify-center shrink-0">
                <FiPlay className="text-white fill-white" size={18} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-xs font-semibold text-gray-500">Дараагийнх{play?.mode === "stream" ? " · автоматаар эхэлнэ" : ""}</span>
                <span className="block text-[15px] font-bold mt-0.5 truncate">{next.title}{next.durationSec ? ` · ${fmtDur(next.durationSec)}` : ""}</span>
              </span>
            </Link>
          )}

          {play?.mode === "stream" && (
            <div className="flex gap-2.5 items-start bg-gray-50 rounded-xl p-3 text-xs leading-relaxed text-gray-600">
              <FiShield className="shrink-0 mt-0.5" size={14} />
              <span>Бичлэг зөвхөн энд тоглогдоно, татаж авах боломжгүй. Дэлгэц дээрх нэр, дугаар нь таны бүртгэлийг тэмдэглэнэ.</span>
            </div>
          )}

          <Link to={backTo} className="text-sm font-semibold text-primary text-center py-1">Бүх хэсгийг харах</Link>
        </div>
      )}
    </div>
  );
}

export default Player;
